import { AlertTriangle, Loader2 } from "lucide-react";
import { programsRangeDays, useEmailPrograms } from "./useEmailPrograms";
import {
  type EmailProgramGroup,
  type EmailProgramReturner,
} from "../../services/EmailMonitoringService";

// Programs worth reading as "did it bring people back". Lifecycle mail
// (onboarding, card notices) shows too, under its own name.
const PROGRAM_LABELS: Record<string, string> = {
  "winback-restore": "Failed-payment restore",
  "winback-missed": "Win-back: what you missed",
  repermission: "Still want deals? (dormant free)",
  "local-roundup": "Weekly roundup",
  "savings-recap": "Monthly recap",
  "onboarding-drip": "Onboarding",
  "checkout-nudge": "Checkout nudge",
  "intro-value": "Intro price reminder",
  "cancel-save": "Cancel save",
  "card-expiring": "Card expiring",
};

// Below this many control-group accounts a lift number is noise.
const MIN_READABLE_HOLDOUT = 30;

function programLabel(key: string): string {
  return PROGRAM_LABELS[key] ?? key;
}

function campaignStep(campaign: string): string {
  const match = campaign.match(/-(\d+)$/);
  return match ? `email ${Number(match[1]) + 1}` : campaign;
}

const shortDate = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function ControlCell({ group }: { group: EmailProgramGroup }) {
  if (group.holdoutAccounts === 0) {
    return <span className="text-gray-400 dark:text-dark-400">none</span>;
  }
  const lift = group.absoluteLift;
  return (
    <span className="text-gray-500 dark:text-dark-300">
      {group.holdoutConverted} of {group.holdoutAccounts}
      {group.treatedAccounts > 0 && lift !== 0 && (
        <span className={`ml-1.5 font-semibold ${lift > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
          {lift > 0 ? "+" : ""}{lift.toFixed(1)} pts
        </span>
      )}
    </span>
  );
}

function ReturnersList({ returners }: { returners: EmailProgramReturner[] }) {
  return (
    <ul className="divide-y divide-gray-100 text-xs dark:divide-dark-700/60">
      {returners.map((r) => (
        <li key={`${r.program}-${r.accountKey}`} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-1.5">
          <span className="min-w-0">
            <span className="font-medium text-gray-900 dark:text-white">{r.userId || "unknown"}</span>
            <span className="text-gray-500 dark:text-dark-300">
              {" · "}{programLabel(r.program)}
              {r.holdout ? " · control group, not emailed" : ` · ${r.emails} emails, last was ${campaignStep(r.lastCampaign)}`}
              {r.clicked && " · clicked"}
            </span>
          </span>
          <span className="text-xs tabular-nums text-gray-500 dark:text-dark-300">
            paid {shortDate(r.convertedAt)} ·{" "}
            {r.payingNow ? (
              <span className="font-semibold text-emerald-700 dark:text-emerald-300">paying{r.plan ? ` ${r.plan}` : ""}</span>
            ) : (
              "not paying now"
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function EmailProgramsPanel({ days }: { days: number }) {
  const rangeDays = programsRangeDays(days);
  const query = useEmailPrograms(days);
  const report = query.data;
  const treated = report?.returners.filter((r) => !r.holdout) ?? [];
  const stillPaying = treated.filter((r) => r.payingNow).length;
  const emailed = report?.programs.reduce((sum, g) => sum + g.treatedAccounts, 0) ?? 0;
  const holdouts = (report?.programs ?? []).map((g) => g.holdoutAccounts).filter((n) => n > 0);
  const smallControl = holdouts.length > 0 && Math.max(...holdouts) < MIN_READABLE_HOLDOUT;

  return (
    <section className="flex min-w-0 flex-col gap-2.5 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-dark-800 dark:ring-dark-700">
      <div>
        <h3 className="text-sm font-semibold text-gray-950 dark:text-white">Did it win anyone back?</h3>
        <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-dark-300">
          Last {rangeDays} days. "Came back" = paid within 30 days of the first email. The control group got no email.
        </p>
      </div>

      {query.isPending ? (
        <div className="flex h-32 items-center justify-center gap-2 text-xs text-gray-500 dark:text-dark-300">
          <Loader2 className="h-4 w-4 animate-spin text-violet-600" /> Loading email programs
        </div>
      ) : query.error || !report ? (
        <div className="flex items-start gap-2 rounded-xl bg-red-50 px-4 py-2 text-xs font-medium text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-300 dark:ring-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Could not load email programs.
        </div>
      ) : (
        <>
          <p className="flex flex-wrap items-baseline gap-x-2.5">
            <span className="font-mono text-2xl font-medium tabular-nums text-gray-950 dark:text-white">{treated.length}</span>
            <span className="text-xs text-gray-700 dark:text-dark-100">
              of {emailed.toLocaleString()} people came back
              {treated.length > 0 && ` · ${stillPaying} still paying`}
            </span>
          </p>

          {report.programs.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-dark-300">No program emails in this range yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[440px] text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-gray-500 dark:border-dark-700 dark:text-dark-300">
                    <th scope="col" className="py-2 pr-3 font-medium">Program</th>
                    <th scope="col" className="py-2 pr-3 text-right font-medium">Emailed</th>
                    <th scope="col" className="py-2 pr-3 text-right font-medium">Opened</th>
                    <th scope="col" className="py-2 pr-3 text-right font-medium">Came back</th>
                    <th scope="col" className="py-2 text-right font-medium">Control</th>
                  </tr>
                </thead>
                <tbody className="font-mono tabular-nums">
                  {report.programs.map((g) => (
                    <tr key={g.key} className="border-b border-gray-100 last:border-0 dark:border-dark-700/60">
                      <td className="py-2 pr-3 font-sans text-gray-900 dark:text-white">{programLabel(g.key)}</td>
                      <td className="py-2 pr-3 text-right text-gray-700 dark:text-dark-100">{g.treatedAccounts.toLocaleString()}</td>
                      <td className="py-2 pr-3 text-right text-gray-700 dark:text-dark-100">{Math.round(g.openPct)}%</td>
                      <td className={`py-2 pr-3 text-right ${g.treatedConverted > 0 ? "font-semibold text-emerald-700 dark:text-emerald-300" : "text-gray-900 dark:text-white"}`}>
                        {g.treatedConverted}
                      </td>
                      <td className="py-2 text-right"><ControlCell group={g} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {treated.length > 0 || report.returners.length > 0 ? (
            <div>
              <h4 className="mb-1 text-xs font-medium text-gray-500 dark:text-dark-300">People who came back</h4>
              <ReturnersList returners={report.returners} />
            </div>
          ) : null}

          {smallControl && (
            <p className="text-xs leading-relaxed text-gray-500 dark:text-dark-300">
              Control groups are {Math.min(...holdouts)}–{Math.max(...holdouts)} people — too small to read lift from yet.
            </p>
          )}
        </>
      )}
    </section>
  );
}
